import { useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  CloudDownload,
  Download,
  File,
  FileUp,
  FolderOpen,
  Gauge,
  Globe2,
  Headphones,
  History,
  Laptop,
  LockKeyhole,
  Monitor,
  MonitorCheck,
  MousePointer2,
  Network,
  Radio,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  UsersRound,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import Reveal from "./Reveal";
import Breadcrumbs from "./Breadcrumbs";

const APP_URL = import.meta.env.VITE_PRODUCT_APP_URL || "#product";
const DOWNLOAD_URL =
  import.meta.env.VITE_ASSIST_DOWNLOAD_URL ||
  "https://connect.cullath.com/desktop/Cullath.exe";

const DEMO_URL =
  import.meta.env.VITE_DEMO_URL ||
  "/contact-us";

const features = [
  {
    icon: MonitorCheck,
    title: "Remote Access",
    copy: "Securely connect to approved devices from anywhere and start providing remote IT support in seconds.",
    visual: "access",
  },
  {
    icon: MousePointer2,
    title: "Remote Control",
    copy: "Navigate the remote desktop with responsive keyboard and pointer control.",
    visual: "control",
  },
  {
    icon: FileUp,
    title: "Secure File Transfer",
    copy: "Send and receive files inside the session without switching applications.",
    visual: "files",
  },
  {
    icon: History,
    title: "Connection History",
    copy: (
    <>
      Review{" "}
      <a
        href="https://cullath.com/desk"
        className="highlight-link"
      >
        remote support sessions
      </a>
      , device activity, and connection details in one place.
    </>
  ),
    visual: "history",
  },
  {
    icon: ShieldCheck,
    title: "Protected Sessions",
    copy: "Use encrypted, request-based connections designed around user consent.",
    visual: "security",
  },
  {
    icon: BarChart3,
    title: "Session Management",
    copy: "See active sessions, connection health, duration, and recent activity.",
    visual: "sessions",
  },
];

const benefitCards = [
  {
    icon: Zap,
    title: "Resolve Issues Faster",
    copy: "Move from a reported issue to a live remote support session without requiring users to install separate tools.",
    variant: "speed",
  },
  {
    icon: LockKeyhole,
    title: "Secure Remote Access",
    copy: "Protect every support interaction with explicit approval, encrypted connections, and visible session status.",
    variant: "secure",
  },
  {
    icon: Globe2,
    title: "Support From Anywhere",
    copy: (
    <>
      Help employees across offices, remote locations, and hybrid teams from {" "}
      <a
        href="https://cullath.com/connect"
        className="highlight-link"
        target="_blank"
        rel="noopener noreferrer"
      >
          one connected workspace.
      </a>{" "}
    
    </>
  ),
    variant: "global",
  },
  {
    icon: Gauge,
    title: "Boost IT Efficiency",
    copy: (
    <>
     Keep technicians focused by combining screen control, file transfer,{" "}
      <a
        href="https://cullath.com/desk"
        className="highlight-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        session tracking,
      </a> and follow-up in one place.{" "}
    
    </>
  ),
    variant: "efficiency",
  },
];

const workspaceModes = [
  { key: "sessions", label: "Session management", icon: BarChart3 },
  { key: "desktop", label: "Remote desktop", icon: Monitor },
  { key: "control", label: "Remote control", icon: MousePointer2 },
  { key: "files", label: "Secure file transfer", icon: FolderOpen },
];

function BrandMark({ compact = false }) {
  return (
    <span className={`ra-brand-lockup ${compact ? "ra-brand-lockup--compact" : ""}`}>
      <img src="/1.png" alt="" aria-hidden="true" />
      <span><strong>Cullath</strong> Assist</span>
    </span>
  );
}

function DesktopWallpaper({ compact = false }) {
  return (
    <div className={`ra-wallpaper ${compact ? "ra-wallpaper--compact" : ""}`} aria-hidden="true">
      <div className="ra-wallpaper__orb ra-wallpaper__orb--one" />
      <div className="ra-wallpaper__orb ra-wallpaper__orb--two" />
      <div className="ra-wallpaper__orb ra-wallpaper__orb--three" />
      {!compact && (
        <>
          <span className="ra-desktop-icon ra-desktop-icon--one"><Monitor size={16} /> This PC</span>
          <span className="ra-desktop-icon ra-desktop-icon--two"><FolderOpen size={16} /> Support Files</span>
        </>
      )}
      <div className="ra-taskbar">
        <i /><i /><i /><i /><i />
      </div>
    </div>
  );
}

function TransferPanel({ compact = false }) {
  const files = compact
    ? [["report.pdf", "Done", 100], ["setup.exe", "68%", 68]]
    : [["diagnostic_tool.exe", "45%", 45], ["support_notes.pdf", "Queued", 8]];

  return (
    <div className={`ra-transfer-panel ${compact ? "ra-transfer-panel--compact" : ""}`}>
      <div className="ra-panel-title"><strong>File Transfer</strong><span>•••</span></div>
      {files.map(([name, status, progress]) => (
        <div className="ra-transfer-row" key={name}>
          <File size={14} />
          <div><strong>{name}</strong><span><i style={{ width: `${progress}%` }} /></span></div>
          <small>{status}</small>
        </div>
      ))}
    </div>
  );
}

function MiniFeatureVisual({ type }) {
  if (type === "access") {
    return (
      <div className="ra-mini-visual ra-mini-access">
        <div className="ra-mini-screen"><DesktopWallpaper compact /></div>
        <span><Wifi size={13} /> Connected</span>
      </div>
    );
  }

  if (type === "control") {
    return (
      <div className="ra-mini-visual ra-mini-control">
        <div className="ra-mini-toolbar"><MousePointer2 size={14} /><Monitor size={14} /><RefreshCw size={14} /><span>•••</span></div>
        <div className="ra-pointer-ripple"><MousePointer2 size={24} /></div>
      </div>
    );
  }

  if (type === "files") {
    return <div className="ra-mini-visual"><TransferPanel compact /></div>;
  }

  if (type === "history") {
    return (
      <div className="ra-mini-visual ra-mini-history">
        {[
          ["HR-PC-014", "Completed", "Today · 10:42 AM"],
          ["FIN-LAPTOP-07", "Completed", "Yesterday · 4:18 PM"],
          ["DEV-PC-21", "Ended", "Jul 11 · 2:05 PM"],
        ].map(([device, status, time]) => (
          <div key={device}><History size={14} /><span><strong>{device}</strong><small>{time}</small></span><em>{status}</em></div>
        ))}
      </div>
    );
  }

  if (type === "security") {
    return (
      <div className="ra-mini-visual ra-mini-security">
        <div className="ra-security-orbit"><ShieldCheck size={38} /><i /><i /><i /></div>
        <strong>Secure Connection</strong>
        <span>AES-256 encrypted</span>
      </div>
    );
  }

  return (
    <div className="ra-mini-visual ra-mini-sessions">
      {["HR-PC-014", "FIN-LAPTOP-07", "DEV-PC-21"].map((name, index) => (
        <div key={name}><Monitor size={13} /><span>{name}</span><small>00:{15 + index * 6}:24</small><i /></div>
      ))}
    </div>
  );
}

function HeroAppPreview() {
  return (
    <div className="ra-hero-app">
      <div className="ra-hero-app__top">
        <BrandMark compact />
        <div className="ra-session-state"><i /> Connected <span>00:12:48</span></div>
        <div className="ra-window-actions"><Clock3 size={14} /><span>•••</span><X size={14} /></div>
      </div>
      <div className="ra-hero-app__body">
        <aside className="ra-app-sidebar">
          <span className="is-active"><MonitorCheck size={17} /> Session</span>
          <span><Monitor size={17} /> Devices</span>
          <span><FolderOpen size={17} /> Files</span>
          <span><Network size={17} /> Connection</span>
          <span><History size={17} /> History</span>
          <span><Settings size={17} /> Settings</span>
        </aside>
        <div className="ra-device-summary">
          <small>Connected to</small>
          <p>HR-PC-014 <i /></p>
          <p>Windows 11 Pro · 192.168.1.45</p>
          <div className="ra-device-status-list">
            <span><MonitorCheck size={15} /><b>Remote Control</b><em>Active</em></span>
            <span><Radio size={15} /><b>Screen Sharing</b><em>Active</em></span>
            <span><FileUp size={15} /><b>File Transfer</b><em>Available</em></span>
            <span><Network size={15} /><b>Connection Health</b><em>Stable</em></span>
          </div>
          <button type="button">End Session</button>
        </div>
        <div className="ra-remote-canvas">
          <div className="ra-floating-toolbar">
            <MousePointer2 size={15} /><Monitor size={15} /><RefreshCw size={15} /><FolderOpen size={15} /><span>•••</span>
          </div>
          <DesktopWallpaper />
        </div>
      </div>
    </div>
  );
}

function WorkspaceActivity({ activeMode }) {
  if (activeMode === "sessions") {
    return (
      <div className="ra-workspace-activity ra-workspace-connect">
        <div className="ra-connect-card">
          <span className="ra-activity-eyebrow"><ShieldCheck size={13} /> Start secure support</span>
          <h4>Connect to a remote device</h4>
          <p>Enter the user&apos;s Assist ID and one-time passcode.</p>
          <label><small>Remote device ID</small><div><Monitor size={14} /><strong>847 295 631</strong></div></label>
          <label><small>Session passcode</small><div><LockKeyhole size={14} /><strong>••••••</strong></div></label>
          <button type="button"><Radio size={14} /> Connect securely <ArrowRight size={14} /></button>
          <em><i /> Waiting for user approval.</em>
        </div>
      </div>
    );
  }

  if (activeMode === "desktop") {
    return (
      <div className="ra-workspace-activity ra-workspace-desktop-live">
        <DesktopWallpaper />
        <div className="ra-desktop-connected-toast"><CheckCircle2 size={15} /><span><b>Remote desktop connected</b><small>Michael&apos;s Windows 11 device is ready</small></span></div>
        <div className="ra-desktop-window">
          <div><span /><span /><span /><b>System Settings</b></div>
          <section><MonitorCheck size={27} /><strong>You now have secure desktop access</strong><small>View the screen and assist the user in real time.</small></section>
        </div>
      </div>
    );
  }

  if (activeMode === "control") {
    return (
      <div className="ra-workspace-activity ra-workspace-control-live">
        <DesktopWallpaper />
        <div className="ra-copy-dialog">
          <div><File size={18} /><span><b>Copying support_patch.zip</b><small>Downloads → Support Files</small></span><strong>72%</strong></div>
          <span><i /></span>
        </div>
        <div className="ra-live-editor">
          <div><i /><i /><i /><b>support-notes.txt</b></div>
          <p>Issue confirmed: printer service stopped.</p>
          <p>Restarting service and applying patch<span className="ra-typing-caret" /></p>
        </div>
        <MousePointer2 className="ra-animated-cursor" size={24} />
        <span className="ra-cursor-click" />
      </div>
    );
  }

  return (
    <div className="ra-workspace-activity ra-workspace-files-live">
      <DesktopWallpaper />
      <div className="ra-file-source">
        <small>Your computer</small>
        <div><File size={18} /><span><b>diagnostic-report.pdf</b><small>2.8 MB</small></span></div>
      </div>
      <div className="ra-file-dropzone"><FileUp size={22} /><b>Drop file on remote device</b><small>Encrypted transfer</small></div>
      <div className="ra-dragged-file"><File size={17} /><span>diagnostic-report.pdf</span></div>
      <div className="ra-transfer-success"><CheckCircle2 size={15} /> Transfer started securely</div>
    </div>
  );
}

function WorkspacePreview({ activeMode }) {
  return (
    <div className={`ra-workspace-product is-${activeMode}`}>
      <div className="ra-workspace-product__top">
        <BrandMark compact />
        <div><small>Connected to</small><strong>DESKTOP-7G2H3KQ</strong></div>
        <div className="ra-workspace-security"><ShieldCheck size={14} /><span><small>Secure Connection</small><strong>AES-256 Encrypted</strong></span></div>
        <div className="ra-workspace-timer"><Clock3 size={14} /><span><small>Session Duration</small><strong>00:24:36</strong></span></div>
        <div className="ra-technician"><span>JD</span><b>John Davis</b></div>
      </div>
      <div className="ra-workspace-product__body">
        <aside className={`ra-workspace-nav ${activeMode === "sessions" ? "is-highlighted" : ""}`}>
          <span><img src="/1.png" alt="" /></span>
          <a className="is-active" href="#workspace"><BarChart3 size={15} /> Active Sessions <em>3</em></a>
          <a href="#workspace"><Monitor size={15} /> Devices</a>
          <a href="#workspace"><UsersRound size={15} /> Contacts</a>
          <a href="#workspace"><History size={15} /> History</a>
          <a href="#workspace"><Settings size={15} /> Settings</a>
          <div><b>JD</b><span>John Davis<small>IT Technician</small></span></div>
        </aside>
        <div className="ra-session-list">
          <div className="ra-session-list__head"><strong>Active Sessions</strong><span>3</span></div>
          <label><Search size={13} /><span>Search sessions</span></label>
          {[
            ["DESKTOP-7G2H3KQ", "Windows 11 Pro", "00:24:36", true],
            ["LAPTOP-3F8J2LM", "Windows 10 Pro", "00:15:42", false],
            ["MACBOOK-8K91D2", "macOS Ventura", "00:06:11", false],
          ].map(([name, os, time, selected]) => (
            <article className={selected ? "is-selected" : ""} key={name}>
              <Monitor size={15} /><span><strong>{name}</strong><small>{os}</small></span><em>{time}</em>
            </article>
          ))}
        </div>
        <div className={`ra-workspace-screen ${activeMode === "desktop" || activeMode === "control" ? "is-highlighted" : ""}`}>
          <div className={`ra-workspace-toolbar ${activeMode === "control" ? "is-highlighted" : ""}`}>
            <span><MousePointer2 size={14} /> Select</span><span><Monitor size={14} /> Control</span><span><RefreshCw size={14} /> View</span><span><FolderOpen size={14} /> Files</span><button type="button">End Session</button>
          </div>
          <WorkspaceActivity activeMode={activeMode} />
          <div className="ra-device-info">
            <strong>Device Info</strong>
            <span><small>User</small>Michael Smith</span>
            <span><small>Operating System</small>Windows 11 Pro</span>
            <span><small>IP Address</small>192.168.1.45</span>
          </div>
        </div>
        <aside className="ra-workspace-sidepanels">
          <div className={activeMode === "files" ? "is-highlighted" : ""}><TransferPanel /></div>
        </aside>
      </div>
    </div>
  );
}

function BenefitVisual({ variant }) {
  if (variant === "speed") {
    return (
      <div className="ra-benefit-flow">
        <span><Zap size={19} /><small>Issue detected</small></span><i />
        <span><MonitorCheck size={21} /><small>Connected</small></span><i />
        <span><CheckCircle2 size={21} /><small>Resolved</small></span>
      </div>
    );
  }

  if (variant === "secure") {
    return (
      <div className="ra-benefit-secure">
        <Laptop size={43} /><i><LockKeyhole size={30} /></i><Monitor size={43} />
        <span><ShieldCheck size={18} /> Encrypted connection</span>
      </div>
    );
  }

  if (variant === "global") {
    return (
      <div className="ra-benefit-globe">
        <Globe2 size={94} />
        <span className="one"><Monitor size={17} /> Office</span>
        <span className="two"><Laptop size={17} /> Home</span>
        <span className="three"><UsersRound size={17} /> Branch</span>
      </div>
    );
  }

  return (
    <div className="ra-benefit-chart">
      <div className="ra-benefit-chart__line"><b>Tickets resolved</b><svg viewBox="0 0 240 70" preserveAspectRatio="none"><polyline points="4,58 25,52 45,55 66,40 88,44 110,29 132,35 154,20 176,27 198,10 218,16 236,5" /></svg></div>
      <div><strong>-32%</strong><small>Downtime</small></div><div><strong>+38%</strong><small>Efficiency</small></div>
    </div>
  );
}

export default function RemoteAccessPage() {
  const [activeMode, setActiveMode] = useState("control");
useEffect(() => {
  window.scrollTo(0, 0);

  document.title = "Remote Desktop Software for IT Support | Cullath Assist";

  const description =
    "Secure remote desktop software for IT support with remote access, screen control, file transfer, and encrypted connections to help IT teams resolve issues faster.";

  // Meta description
  let metaDescription = document.querySelector('meta[name="description"]');

  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.setAttribute("name", "description");
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute("content", description);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": "https://cullath.com/assist/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://cullath.com/"
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Assist",
          item: "https://cullath.com/assist"
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": "https://cullath.com/assist/#software",
      name: "Cullath Assist",
      url: "https://cullath.com/assist",
      description,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      brand: {
        "@id": "https://cullath.com/#organization"
      },
      isPartOf: {
        "@id": "https://cullath.com/#website"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://cullath.com/assist/#webpage",
      url: "https://cullath.com/assist",
      name: "Remote Desktop Software for IT Support | Cullath Assist",
      description,
      isPartOf: {
        "@id": "https://cullath.com/#website"
      },
      about: {
        "@id": "https://cullath.com/assist/#software"
      },
      inLanguage: "en-US"
    }
  ];

  const scripts = schemas.map((schema) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return script;
  });

  return () => {
    scripts.forEach((script) => script.remove());
  };
}, []);
  
  useEffect(() => {
  const canonicalUrl = "https://cullath.com/assist";

  let canonical = document.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", canonicalUrl);

  return () => {
    canonical.remove();
  };
}, []);
  return (
    <div className="remote-access-page-shell">
      <SiteHeader isHeroPage />
      <main className="ra-page">
        <section className="ra-hero" id="product">
          <div className="ra-orbit-decoration ra-orbit-decoration--hero" aria-hidden="true"><i /><i /><i /></div>
          <div className="ra-container ra-hero__layout">
            <Reveal className="ra-hero__copy">
              <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Products", href: "/#product" }, { label: "Assist" }]} />
              <div className="ra-product-pill"><Headphones size={17} /> CULLATH ASSIST</div>
              <h1>Secure Remote Desktop <br /><span>Software for IT Support</span></h1>
              <p>
                Securely access approved devices, troubleshoot issues, transfer files, and deliver real-time IT support from one connected Cullath workspace.</p>
              <div className="ra-hero__actions">
                <a
                  className="ra-button ra-button--primary"
                  href={DEMO_URL}
                >
                  <Clock3 size={18} />
                  Request a Demo
                </a>

                <a
                  className="ra-button ra-button--secondary"
                  href={DOWNLOAD_URL}
                >
                  <Download size={18} />
                  Download
                </a>
              </div>
              <div className="ra-hero__proof">
                <span><ShieldCheck size={19} /><b>Secure</b><small>request-based access</small></span>
                <span><LockKeyhole size={19} /><b>Protected</b><small>encrypted sessions</small></span>
                <span><Zap size={19} /><b>Fast</b><small>real-time support</small></span>
                <span><UsersRound size={19} /><b>Connected</b><small>built for IT teams</small></span>
              </div>
            </Reveal>

            <Reveal className="ra-hero__visual" delay={120}>
              <HeroAppPreview />
              <div className="ra-connection-flow">
                <article><span><Laptop size={24} /></span><div><strong>Your Device</strong><small>Technician</small></div></article>
                <i />
                <article className="ra-connection-flow__secure"><span><ShieldCheck size={22} /></span><div><strong>Secure Connection</strong><small>AES-256 encrypted</small></div></article>
                <i />
                <article><span><Monitor size={24} /></span><div><strong>Remote Device</strong><small>HR-PC-014</small></div></article>
              </div>
            </Reveal>
          </div>
          <Reveal className="ra-platforms" delay={180}>
            <span>Works across platforms</span>
            <div><b>Windows</b><b>Android</b><b>iOS</b></div>
          </Reveal>
        </section>

        <section className="ra-section ra-features" id="features">
          <div className="ra-orbit-decoration ra-orbit-decoration--left" aria-hidden="true"><i /><i /></div>
          <div className="ra-orbit-decoration ra-orbit-decoration--right" aria-hidden="true"><i /><i /></div>
          <div className="ra-container">
            <Reveal className="ra-section-heading">
              <span>FEATURES</span>
              <h2>Everything You Need in Remote Desktop  <em>Software for IT Support</em></h2>
              <p>From secure remote access and screen control to file transfer, connection history, and  <a href="https://cullath.com/desk"
                className="highlight-link">
                session management,
              </a>
                Cullath Assist brings every tool needed for efficient IT support into one secure workspace.</p>
            </Reveal>
            <div className="ra-feature-grid">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Reveal delay={(index % 3) * 70} key={feature.title}>
                    <article className={`ra-feature-card ra-feature-card--${feature.visual}`}>
                      <div className="ra-feature-card__visual"><MiniFeatureVisual type={feature.visual} /></div>
                      <div className="ra-feature-card__copy">
                        <span><Icon size={21} /></span>
                        <div><h3>{feature.title}</h3><p>{feature.copy}</p></div>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
            <Reveal className="ra-section-note"><ShieldCheck size={18} /> Built for IT teams. Designed for <b>speed.</b> Secured for your business.</Reveal>
          </div>
        </section>

        <section className="ra-section ra-showcase" id="workspace">
          <div className="ra-container ra-container--wide">
            <Reveal className="ra-section-heading">
              <span>PRODUCT SHOWCASE</span>
              <h2>Explore the Remote  <em>Desktop Workspace</em></h2>
              <p>Monitor secure connections, remote control, file transfers, and <a href="https://cullath.com/desk"
              className="highlight-link">
           active sessions
            </a> from one centralized remote support workspace.</p>
            </Reveal>

            <Reveal className="ra-showcase-stage" delay={90}>
              <div className="ra-showcase-tabs" role="tablist" aria-label="Assist workspace preview">
                {workspaceModes.map((mode) => {
                  const Icon = mode.icon;
                  return (
                    <button key={mode.key} type="button" className={activeMode === mode.key ? "is-active" : ""} onClick={() => setActiveMode(mode.key)}>
                      <Icon size={16} /> {mode.label}
                    </button>
                  );
                })}
              </div>
              <WorkspacePreview activeMode={activeMode} />
              <div className="ra-showcase-callout ra-showcase-callout--left">
                <BarChart3 size={18} /><div><strong>Session management</strong><p>Track every active support connection from one view.</p></div>
              </div>
              <div className="ra-showcase-callout ra-showcase-callout--right">
                <FolderOpen size={18} /><div><strong>Secure file transfer</strong><p>Send and receive files without leaving the support session.</p></div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="ra-section ra-benefits" id="why">
          <div className="ra-orbit-decoration ra-orbit-decoration--left" aria-hidden="true"><i /><i /></div>
          <div className="ra-container">
            <Reveal className="ra-section-heading">
              <span>BUSINESS BENEFITS</span>
              <h2>Why IT Teams Choose Cullath <em>Assist for Remote IT Support</em></h2>
              <p>Resolve issues faster, reduce downtime, and deliver secure remote IT support for employees wherever they work.</p>
            </Reveal>
            <div className="ra-benefit-grid">
              {benefitCards.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Reveal delay={(index % 2) * 90} key={benefit.title}>
                    <article className={`ra-benefit-card ra-benefit-card--${benefit.variant}`}>
                      <div className="ra-benefit-card__visual"><BenefitVisual variant={benefit.variant} /></div>
                      <div className="ra-benefit-card__copy">
                        <span><Icon size={22} /></span>
                        <h3>{benefit.title}</h3>
                        <p>{benefit.copy}</p>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
            <Reveal className="ra-section-note"><ShieldCheck size={18} /> Enterprise-grade security. Built for performance. <b>Trusted by modern IT teams.</b></Reveal>
          </div>
        </section>

        <section className="ra-section ra-get-started" id="get-started">
          <div className="ra-container">
            <Reveal className="ra-section-heading">
              <span>GET STARTED</span>
              <h2>Ready to transform your <em>Remote IT Support Experience?</em></h2>
              <p>See how Cullath Assist helps your IT team resolve issues faster, securely support remote users, and keep business moving with one powerful remote support solution.</p>
            </Reveal>

            <div className="ra-start-grid">
              <Reveal>
                <article className="ra-start-card ra-start-card--demo">
                  <div className="ra-start-card__icon"><Clock3 size={28} /></div>
                  <div className="ra-start-card__copy"><h3>Request a Demo</h3><p>Get a personalized walkthrough tailored to your remote IT support workflow.</p></div>
                  <ul><li><Check size={15} /> Personalized live demo</li><li><Check size={15} /> Solutions tailored to your team</li><li><Check size={15} /> Expert guidance from Cullath</li></ul>
                  <a href={DEMO_URL}>Request Demo <ArrowRight size={17} /></a>
                  <div className="ra-start-card__device"><DesktopWallpaper compact /></div>
                </article>
              </Reveal>
              <Reveal delay={80}>
                <article className="ra-start-card ra-start-card--download">
                  <div className="ra-start-card__icon"><CloudDownload size={28} /></div>
                  <div className="ra-start-card__copy"><h3>Download Assist</h3><p>Install Cullath Assist and start delivering secure remote IT support in minutes.</p></div>
                  <ul><li><Check size={15} /> Easy to install</li><li><Check size={15} /> Lightweight and protected</li><li><Check size={15} /> Available for Windows</li></ul>
                  <a href={DOWNLOAD_URL}>Download for Windows <Download size={17} /></a>
                  <span className="ra-system-link">View system requirements <ChevronRight size={16} /></span>
                </article>
              </Reveal>
            </div>

            <Reveal className="ra-trust-strip" delay={100}>
              <span><ShieldCheck size={23} /><div><b>Enterprise security</b><small>Protected remote sessions</small></div></span>
              <span><Network size={23} /><div><b>Reliable performance</b><small>Stable support connections</small></div></span>
              <span><UsersRound size={23} /><div><b>Built for IT teams</b><small>One connected workspace</small></div></span>
              <span><LockKeyhole size={23} /><div><b>Privacy first</b><small>User-approved access</small></div></span>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
